import { getExposedThemeProperties, isThemePathExposedInApi, type ThemeProperty } from '@repo/lib';
import { z } from '../zod';
import { buildApiLeafSchema, buildThemeVariablesSchema } from './buildThemeVariablesSchema';

const VARIABLES_PREFIX = 'variables.';

export const ThemeOverridesInputValidationSchema = buildThemeVariablesSchema({
  mode: 'apiInput',
  include: isThemePathExposedInApi,
}).meta({
  id: 'ThemeOverridesInput',
  description:
    "The page's design and labels, set on top of the page's theme. Each key is a theme property, nested the same way the theme stores it, for example " +
    '`{ "tokens": { "colorPrimary": { "light": "#1a2b3c" } }, "labels": { "payButtonText": "Buy now" } }`. ' +
    "Only the keys you send change; keys you leave out keep their current value. `null` at any level resets every property under it to the page theme's default. " +
    'Color values are `{ light, dark }`, one color for light mode and one for dark mode. Images take the `file` ID of an image you uploaded. ' +
    '`customCss` needs a plan that includes custom CSS, and the `elements.poweredByCheckoutPage` and `elements.poweredByStripe` badges can only be changed with a paid subscription. ' +
    'Order bump styling is not available here.',
});

export const ThemeOverridesResponseValidationSchema = buildThemeVariablesSchema({
  mode: 'apiOutput',
  include: isThemePathExposedInApi,
}).meta({
  id: 'ThemeOverrides',
  description:
    "The design and labels the page sets on top of its theme. Properties the page doesn't override are left out and follow the theme. Uses the same shape as the `themeOverrides` input.",
});

export type ThemeOverridesInput = z.infer<typeof ThemeOverridesInputValidationSchema>;

// MCP clients load every tool's schemas on tools/list, and the typed schema adds about 100KB to each page tool.
// So the MCP tools advertise themeOverrides as any object, point the agent at list_theme_properties, and leave
// validation to the API.
export const ThemeOverridesToolInputValidationSchema = z
  .object({})
  .catchall(z.unknown())
  .nullish()
  .meta({
    description:
      "The page's design and labels, set on top of its theme and nested by theme property path: `tokens.colorPrimary` is sent as " +
      '`{ "tokens": { "colorPrimary": { "light": "#1A73E8" } } }`. Keys you leave out keep their current value, and `null` resets a property, or everything under a key, to the theme default. ' +
      'Call `list_theme_properties` for the supported properties, their value formats and, where a property has a fixed one, its default.',
  });

export const ThemeOverridesToolResponseValidationSchema = z
  .object({})
  .catchall(z.unknown())
  .nullish()
  .meta({
    description:
      'The design and labels the page sets on top of its theme, nested by theme property path. Properties the page does not override are left out. ' +
      'Has the same shape as the `themeOverrides` input, so it can be sent back as is. `list_theme_properties` describes each property.',
  });

type LeafParseResult = { success: true; data: unknown } | { success: false };

let outputLeavesByPath: Map<string, { property: ThemeProperty; schema: z.ZodTypeAny }> | undefined;

function getOutputLeaves() {
  if (!outputLeavesByPath) {
    outputLeavesByPath = new Map(
      getExposedThemeProperties().map(property => [
        property.path.slice(VARIABLES_PREFIX.length),
        { property, schema: buildApiLeafSchema(property, 'apiOutput') },
      ]),
    );
  }
  return outputLeavesByPath;
}

const COLOR_MODES = ['light', 'dark'] as const;

/**
 * Validates one stored theme value for the seller API response, by its path relative to `variables.`
 * (e.g. `tokens.colorPrimary`). Fails for paths the API does not expose and for values it would not accept
 * as input, so a response only ever carries what a client can send back. A color keeps its valid modes.
 */
export function parseThemeOverrideLeafForApi(path: string, value: unknown): LeafParseResult {
  const leaf = getOutputLeaves().get(path);
  if (!leaf || value == null) return { success: false };

  if (leaf.property.type === 'color') {
    if (typeof value !== 'object' || Array.isArray(value)) return { success: false };

    const color: Record<string, string> = {};
    for (const mode of COLOR_MODES) {
      const result = leaf.schema.safeParse({ [mode]: (value as Record<string, unknown>)[mode] });
      const parsed = result.success
        ? (result.data as Record<string, unknown> | null | undefined)?.[mode]
        : undefined;
      if (typeof parsed === 'string') color[mode] = parsed;
    }

    return Object.keys(color).length > 0 ? { success: true, data: color } : { success: false };
  }

  const result = leaf.schema.safeParse(value);
  return result.success && result.data != null
    ? { success: true, data: result.data }
    : { success: false };
}
